import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Eye, Printer, Send } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { useSnackbar } from 'notistack';
import DataTable, {
    type ActionConfig,
    type Column,
    type FilterOption,
} from '@/components/ui/data-table';
import {
    formatDateTime,
    getChannelIcon,
    getRecipientCrmPath,
    getTypeColor,
    resolveRecipientName,
    type Notification,
    useResendNotification,
} from '@/api/notifications/use-notifications.tsx';
import { StatusBadge } from '@/components/shared/StatusBadge';

function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

interface NotificationListProps {
    notifications: Notification[];
    filters: Record<string, any>;
    sortConfig: { key: string; direction: 'asc' | 'desc' };
    isLoading: boolean;
    onFilterChange: (filters: Record<string, any>) => void;
    onSortChange: (key: string, direction: 'asc' | 'desc') => void;
    onRefresh: () => void;
}

export const NotificationList: React.FC<NotificationListProps> = ({
    notifications,
    filters,
    sortConfig,
    isLoading,
    onFilterChange,
    onSortChange,
    onRefresh,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const resendMutation = useResendNotification();
    const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
    const previewRef = useRef<HTMLDivElement>(null);

    const handlePrintView = () => {
        if (!selectedNotification) return;
        const n = selectedNotification;
        const recipientName = resolveRecipientName(n) ?? '';
        const channelsList =
            n.channels
                ?.map(
                    c =>
                        (c as { channel?: string; type?: string }).channel ??
                        (c as { type?: string }).type ??
                        ''
                )
                .join(', ') ?? '';
        const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>${escapeHtml(n.title || 'Notification')}</title>
  <style>
    * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { font-family: system-ui, -apple-system, sans-serif; padding: 2rem; max-width: 800px; margin: 0 auto; color: #111; line-height: 1.5; }
    h1 { font-size: 1.5rem; margin-bottom: 1rem; }
    .meta { background: #f5f5f5; padding: 1rem; border-radius: 6px; margin-bottom: 1rem; }
    .meta p { margin: 0.35rem 0; font-size: 0.9rem; }
    .content { border: 1px solid #e5e5e5; padding: 1rem; border-radius: 6px; }
    .content img { max-width: 100%; height: auto; }
    @media print { body { padding: 0.5rem; } .meta { background: #eee; } }
  </style>
</head>
<body>
  <h1>${escapeHtml(n.title || 'Notification')}</h1>
  <div class="meta">
    ${n.created_by ? `<p><strong>Sender:</strong> ${escapeHtml(n.created_by)}</p>` : ''}
    <p><strong>Recipient:</strong> ${escapeHtml(recipientName)}</p>
    ${n.recipient_email ? `<p><strong>Recipient email:</strong> ${escapeHtml(n.recipient_email)}</p>` : ''}
    <p><strong>Created:</strong> ${escapeHtml(formatDateTime(n.created_at))}</p>
    ${n.sent_at ? `<p><strong>Sent at:</strong> ${escapeHtml(formatDateTime(n.sent_at))}</p>` : ''}
    ${channelsList ? `<p><strong>Channels:</strong> ${escapeHtml(channelsList)}</p>` : ''}
    <p><strong>Type:</strong> ${escapeHtml(n.type)} &nbsp; <strong>Priority:</strong> ${escapeHtml(n.priority)} &nbsp; <strong>Status:</strong> ${escapeHtml(n.status)}</p>
  </div>
  <div class="content">${n.message}</div>
  <script>window.onload = function() { window.print(); };<\/script>
</body>
</html>`;
        const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const w = window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        if (!w) {
            enqueueSnackbar('Please allow pop-ups to open print view', { variant: 'success' });
            return;
        }
        enqueueSnackbar('Print view opened — use Save as PDF in the print dialog', {
            variant: 'success',
        });
    };

    const handleResend = async (notificationId: string) => {
        try {
            await resendMutation.mutateAsync(notificationId);
            enqueueSnackbar('Notification resent successfully', { variant: 'success' });
            setSelectedNotification(null);
            onRefresh();
        } catch (error: any) {
            enqueueSnackbar(error?.response?.data?.message || 'Failed to resend notification', {
                variant: 'error',
            });
        }
    };

    const columns: Column<Notification>[] = [
        {
            id: 'title',
            label: 'Title',
            key: 'title',
            sortable: true,
        },
        {
            id: 'type',
            label: 'Type',
            key: 'type',
            sortable: true,
            render: value => (
                <Badge variant="secondary" className={getTypeColor(value)}>
                    {value}
                </Badge>
            ),
        },
        {
            id: 'priority',
            label: 'Priority',
            key: 'priority',
            sortable: true,
            render: value => <StatusBadge status={value} kind="priority" />,
        },
        {
            id: 'status',
            label: 'Status',
            key: 'status',
            sortable: true,
            render: (value, item) => (
                <div className="flex items-center gap-1.5">
                    <StatusBadge status={value} kind="notification" />
                    {!item.read_at && (
                        <Badge
                            variant="default"
                            className="bg-info text-info-foreground text-[10px] px-1.5 py-0"
                        >
                            New
                        </Badge>
                    )}
                </div>
            ),
        },
        {
            id: 'recipient',
            label: 'Recipient',
            key: 'recipient',
            sortable: true,
            render: (_value, item) => {
                const name = resolveRecipientName(item);
                const path = getRecipientCrmPath(item);
                if (path) {
                    return (
                        <Link
                            to={path}
                            className="text-sm text-primary hover:underline"
                            onClick={e => e.stopPropagation()}
                        >
                            {name}
                        </Link>
                    );
                }
                return <span className="text-sm">{name}</span>;
            },
        },
        {
            id: 'channels',
            label: 'Channels',
            key: 'channels',
            render: value => (
                <div className="flex items-center gap-1">
                    {Array.isArray(value) &&
                        value.map((channel: any) => (
                            <span key={channel.id} className="text-base">
                                {getChannelIcon(channel.channel || channel.type)}
                            </span>
                        ))}
                </div>
            ),
        },
        {
            id: 'created_at',
            label: 'Created',
            key: 'created_at',
            sortable: true,
            render: value => (
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatDateTime(value)}
                </span>
            ),
        },
    ];

    const filterOptions: FilterOption[] = [
        {
            key: 'type',
            label: 'Type',
            type: 'select',
            options: [
                { value: 'system', label: 'System' },
                { value: 'user', label: 'User' },
                { value: 'trading', label: 'Trading' },
                { value: 'compliance', label: 'Compliance' },
                { value: 'marketing', label: 'Marketing' },
            ],
        },
        {
            key: 'priority',
            label: 'Priority',
            type: 'select',
            options: [
                { value: 'low', label: 'Low' },
                { value: 'medium', label: 'Medium' },
                { value: 'high', label: 'High' },
                { value: 'critical', label: 'Critical' },
            ],
        },
        {
            key: 'status',
            label: 'Status',
            type: 'select',
            options: [
                { value: 'pending', label: 'Pending' },
                { value: 'sent', label: 'Sent' },
                { value: 'delivered', label: 'Delivered' },
                { value: 'read', label: 'Read' },
                { value: 'failed', label: 'Failed' },
            ],
        },
        {
            key: 'recipient',
            label: 'Recipient',
            type: 'input',
            placeholder: 'Search by recipient...',
        },
    ];

    const actions: ActionConfig<Notification>[] = [
        {
            id: 'view',
            label: 'View Details',
            icon: Eye,
            variant: 'outline',
            onClick: notification => setSelectedNotification(notification),
        },
    ];

    return (
        <>
            <DataTable
                data={notifications}
                columns={columns}
                isLoading={isLoading}
                sortConfig={{
                    key: sortConfig.key as keyof Notification,
                    direction: sortConfig.direction,
                }}
                onSort={key => {
                    const newDirection =
                        sortConfig.key === String(key) && sortConfig.direction === 'asc'
                            ? 'desc'
                            : 'asc';
                    onSortChange(String(key), newDirection);
                }}
                filters={filters}
                onFilterChange={onFilterChange}
                selectedItems={[]}
                onSelectionChange={() => {}}
                filterOptions={filterOptions}
                actions={actions}
                getItemId={item => item.id}
                emptyMessage="No notifications found"
                showSelection={false}
                showAdvancedFilters={true}
                showColumnSettings={true}
            />

            <Dialog
                open={!!selectedNotification}
                onOpenChange={open => !open && setSelectedNotification(null)}
            >
                <DialogContent className="max-w-2xl flex flex-col max-h-[85vh]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Bell className="h-5 w-5 shrink-0" />
                            <span>{selectedNotification?.title}</span>
                        </DialogTitle>
                    </DialogHeader>

                    {selectedNotification && (
                        <div ref={previewRef} className="flex-1 overflow-y-auto space-y-5 pr-1">
                            {/* Badges */}
                            <div className="flex flex-wrap gap-2">
                                <Badge
                                    variant="secondary"
                                    className={getTypeColor(selectedNotification.type)}
                                >
                                    {selectedNotification.type}
                                </Badge>
                                <StatusBadge
                                    status={selectedNotification.priority}
                                    kind="priority"
                                />
                                <StatusBadge
                                    status={selectedNotification.status}
                                    kind="notification"
                                />
                                {!selectedNotification.read_at && (
                                    <Badge
                                        variant="default"
                                        className="bg-info text-info-foreground"
                                    >
                                        New
                                    </Badge>
                                )}
                            </div>

                            {/* Meta grid: sender, recipient, times (included in PDF) */}
                            <div className="grid grid-cols-2 gap-4 text-sm border rounded-md p-4 bg-muted/30">
                                {selectedNotification.created_by && (
                                    <div>
                                        <p className="text-xs text-muted-foreground mb-1">Sender</p>
                                        <p className="font-medium">
                                            {selectedNotification.created_by}
                                        </p>
                                    </div>
                                )}
                                <div>
                                    <p className="text-xs text-muted-foreground mb-1">Recipient</p>
                                    {getRecipientCrmPath(selectedNotification) ? (
                                        <Link
                                            to={getRecipientCrmPath(selectedNotification)!}
                                            className="font-medium text-primary hover:underline"
                                        >
                                            {resolveRecipientName(selectedNotification)}
                                        </Link>
                                    ) : (
                                        <p className="font-medium">
                                            {resolveRecipientName(selectedNotification)}
                                        </p>
                                    )}
                                </div>
                                {selectedNotification.recipient_email && (
                                    <div>
                                        <p className="text-xs text-muted-foreground mb-1">
                                            Recipient email
                                        </p>
                                        <p className="font-medium">
                                            {selectedNotification.recipient_email}
                                        </p>
                                    </div>
                                )}
                                <div>
                                    <p className="text-xs text-muted-foreground mb-1">Created</p>
                                    <p className="font-medium">
                                        {formatDateTime(selectedNotification.created_at)}
                                    </p>
                                </div>
                                {selectedNotification.sent_at && (
                                    <div>
                                        <p className="text-xs text-muted-foreground mb-1">
                                            Sent at
                                        </p>
                                        <p className="font-medium">
                                            {formatDateTime(selectedNotification.sent_at)}
                                        </p>
                                    </div>
                                )}
                                {selectedNotification.channels &&
                                    selectedNotification.channels.length > 0 && (
                                        <div>
                                            <p className="text-xs text-muted-foreground mb-1">
                                                Channels
                                            </p>
                                            <div className="flex gap-1.5">
                                                {selectedNotification.channels.map(channel => (
                                                    <span key={channel.id} className="text-base">
                                                        {getChannelIcon(
                                                            (channel as any).channel || channel.type
                                                        )}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                            </div>

                            {/* Content */}
                            <div>
                                <p className="text-xs text-muted-foreground mb-2">Content</p>
                                <div
                                    className="border rounded-md p-4 prose prose-sm dark:prose-invert max-w-none overflow-y-auto max-h-64 bg-background"
                                    dangerouslySetInnerHTML={{
                                        __html: selectedNotification.message,
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    <DialogFooter className="mt-4 shrink-0">
                        <Button variant="outline" onClick={() => setSelectedNotification(null)}>
                            Close
                        </Button>
                        {selectedNotification && (
                            <Button variant="outline" onClick={handlePrintView}>
                                <Printer className="h-4 w-4" />
                                Print / Save as PDF
                            </Button>
                        )}
                        {selectedNotification &&
                            (selectedNotification.status === 'failed' ||
                                selectedNotification.status === 'sent') && (
                                <Button
                                    onClick={() => handleResend(selectedNotification.id)}
                                    disabled={resendMutation.isPending}
                                >
                                    <Send className="h-4 w-4" />
                                    {selectedNotification.status === 'failed' ? 'Retry' : 'Resend'}
                                </Button>
                            )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
